import React, { useState, useRef } from "react";

const COLORS = {
  navy: "#0f1f3d",
  navyLight: "#1a3260",
  gold: "#c9a84c",
  goldLight: "#e8c76a",
  surface: "#f7f6f2",
  surfaceAlt: "#eeecea",
  border: "#dedad4",
  borderFocus: "#c9a84c",
  text: "#1a1614",
  textMuted: "#6b6560",
  textLight: "#9b9490",
  danger: "#c0392b",
  white: "#ffffff",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600&family=Mulish:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'Mulish', sans-serif;
    background: #f0ede8;
    background-image:
      radial-gradient(circle at 20% 10%, rgba(201,168,76,0.07) 0%, transparent 50%),
      radial-gradient(circle at 80% 90%, rgba(15,31,61,0.06) 0%, transparent 50%);
    min-height: 100vh;
    padding: 2rem 1rem 4rem;
    color: ${COLORS.text};
  }

  .page-wrap {
    max-width: 680px;
    margin: 0 auto;
  }

  .logo-area {
    text-align: center;
    margin-bottom: 2rem;
  }

  .logo-area img {
    height: 80px;
    object-fit: contain;
    filter: drop-shadow(0 2px 8px rgba(15,31,61,0.12));
  }

  .card {
    background: ${COLORS.white};
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(15,31,61,0.06), 0 8px 32px rgba(15,31,61,0.10);
  }

  .card-header {
    background: ${COLORS.navy};
    background-image: linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.navyLight} 100%);
    padding: 2rem 2.5rem;
    position: relative;
    overflow: hidden;
  }

  .card-header::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${COLORS.gold}, ${COLORS.goldLight}, ${COLORS.gold});
  }

  .card-header h1 {
    font-family: 'Playfair Display', serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: ${COLORS.white};
    line-height: 1.5;
    text-align: center;
  }

  .card-header p {
    text-align: center;
    color: rgba(255,255,255,0.45);
    font-size: 0.72rem;
    margin-top: 0.5rem;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }

  .card-body {
    padding: 2rem 2.5rem 2.5rem;
  }

  @media (max-width: 600px) {
    .card-header { padding: 1.5rem 1.25rem; }
    .card-header h1 { font-size: 1.05rem; }
    .card-body { padding: 1.5rem 1.25rem; }
  }

  .section { margin-bottom: 1.75rem; }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: 0.95rem;
    font-weight: 500;
    color: ${COLORS.navy};
    padding-bottom: 0.55rem;
    margin-bottom: 1.1rem;
    border-bottom: 1.5px solid ${COLORS.border};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .section-title::before {
    content: '';
    display: inline-block;
    width: 3px;
    height: 13px;
    background: ${COLORS.gold};
    border-radius: 2px;
    flex-shrink: 0;
  }

  .row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  @media (max-width: 500px) {
    .row { grid-template-columns: 1fr; }
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.32rem;
    margin-bottom: 1rem;
  }

  .field:last-child { margin-bottom: 0; }

  .field > label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b6560;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .field > label .req { color: #c9a84c; margin-left: 2px; }

  input[type="text"],
  input[type="email"],
  input[type="tel"],
  input[type="number"] {
    font-family: 'Mulish', sans-serif;
    font-size: 0.93rem;
    color: #1a1614;
    background: #f7f6f2;
    border: 1.5px solid #dedad4;
    border-radius: 8px;
    padding: 0.65rem 0.9rem;
    outline: none;
    transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
    width: 100%;
    -webkit-appearance: none;
    appearance: none;
  }

  input[type="text"]:focus,
  input[type="email"]:focus,
  input[type="tel"]:focus,
  input[type="number"]:focus {
    border-color: #c9a84c;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(201,168,76,0.13);
  }


  /* Upload */
  .upload-zone {
    border: 2px dashed ${COLORS.border};
    border-radius: 10px;
    padding: 1.75rem 1.25rem;
    text-align: center;
    cursor: pointer;
    background: ${COLORS.surface};
    transition: border-color 0.18s, background 0.18s;
    position: relative;
  }

  .upload-zone:hover {
    border-color: ${COLORS.gold};
    background: rgba(201,168,76,0.04);
  }

  .upload-zone.has-file {
    border-style: solid;
    border-color: ${COLORS.gold};
    background: rgba(201,168,76,0.04);
  }

  .upload-zone input[type="file"] {
    position: absolute;
    inset: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    padding: 0;
    border: none;
    background: none;
    font-size: 0;
  }

  .upload-icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(201,168,76,0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 0.75rem;
    font-size: 1.4rem;
  }

  .upload-text {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${COLORS.navy};
    margin-bottom: 0.25rem;
  }

  .upload-hint {
    font-size: 0.76rem;
    color: ${COLORS.textLight};
    line-height: 1.5;
  }

  .file-preview {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.85rem;
    padding: 0.65rem 0.85rem;
    background: ${COLORS.white};
    border: 1px solid ${COLORS.border};
    border-radius: 8px;
  }

  .file-preview-thumb {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: cover;
    border: 1px solid ${COLORS.border};
    cursor: pointer;
    flex-shrink: 0;
  }

  .file-preview-icon {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    background: ${COLORS.surface};
    border: 1px solid ${COLORS.border};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  .file-preview-name {
    flex: 1;
    font-size: 0.84rem;
    font-weight: 500;
    color: ${COLORS.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-preview-size {
    font-size: 0.72rem;
    color: ${COLORS.textLight};
    margin-top: 1px;
  }

  .file-remove {
    background: none;
    border: none;
    cursor: pointer;
    color: ${COLORS.textLight};
    font-size: 1.1rem;
    padding: 4px;
    border-radius: 4px;
    line-height: 1;
    transition: color 0.15s, background 0.15s;
    flex-shrink: 0;
  }

  .file-remove:hover {
    color: ${COLORS.danger};
    background: rgba(192,57,43,0.07);
  }

  /* Submit */
  .submit-wrap {
    margin-top: 2rem;
    text-align: center;
  }

  .btn-submit {
    font-family: 'Mulish', sans-serif;
    font-size: 0.93rem;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    background: ${COLORS.navy};
    color: ${COLORS.white};
    border: none;
    border-radius: 10px;
    padding: 0.9rem 3.5rem;
    cursor: pointer;
    transition: background 0.18s, transform 0.12s, box-shadow 0.18s;
    box-shadow: 0 4px 18px rgba(15,31,61,0.22);
    position: relative;
    overflow: hidden;
  }

  .btn-submit::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, ${COLORS.gold}, ${COLORS.goldLight});
    transform: scaleX(0);
    transition: transform 0.25s;
  }

  .btn-submit:hover:not(:disabled)::after { transform: scaleX(1); }
  .btn-submit:hover:not(:disabled) {
    background: ${COLORS.navyLight};
    box-shadow: 0 6px 26px rgba(15,31,61,0.28);
  }
  .btn-submit:active:not(:disabled) { transform: scale(0.98); }
  .btn-submit:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }

  /* Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(10,15,30,0.88);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
  }

  .modal-inner {
    position: relative;
    max-width: 92vw;
    max-height: 90vh;
  }

  .modal-inner img {
    border-radius: 10px;
    max-width: 100%;
    max-height: 82vh;
    object-fit: contain;
    display: block;
  }

  .modal-close {
    position: absolute;
    top: -14px; right: -14px;
    width: 32px; height: 32px;
    border-radius: 50%;
    background: ${COLORS.white};
    border: none;
    cursor: pointer;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${COLORS.navy};
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    transition: background 0.15s;
  }

  .modal-close:hover { background: ${COLORS.surface}; }

  /* Success */
  .success-card {
    background: ${COLORS.white};
    border-radius: 16px;
    padding: 4rem 2rem;
    text-align: center;
    box-shadow: 0 8px 40px rgba(15,31,61,0.12);
  }

  .success-icon { font-size: 3.5rem; margin-bottom: 1.1rem; }

  .success-card h2 {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    color: ${COLORS.navy};
    margin-bottom: 0.75rem;
  }

  .success-card p {
    color: ${COLORS.textMuted};
    font-size: 0.92rem;
    max-width: 380px;
    margin: 0 auto;
    line-height: 1.65;
  }

  .error-msg {
    font-size: 0.76rem;
    color: ${COLORS.danger};
    margin-top: 0.3rem;
  }

  /* Izjave */
  .izjave-box {
    background: ${COLORS.surface};
    border: 1.5px solid ${COLORS.border};
    border-radius: 10px;
    overflow: hidden;
  }

  .izjave-header {
    background: rgba(15,31,61,0.04);
    border-bottom: 1px solid ${COLORS.border};
    padding: 0.7rem 1rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${COLORS.navy};
  }

  .izjave-box.has-error {
    border-color: ${COLORS.danger};
  }

  .check-row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.9rem 1rem;
    cursor: pointer;
    transition: background 0.12s;
  }

  .check-row:not(:last-child) {
    border-bottom: 1px solid ${COLORS.surfaceAlt};
  }

  .check-row:hover { background: rgba(201,168,76,0.04); }

  .check-row input[type="checkbox"] {
    width: 18px;
    height: 18px;
    min-width: 18px;
    border: 2px solid ${COLORS.border};
    border-radius: 4px;
    background: white;
    cursor: pointer;
    accent-color: ${COLORS.navy};
    margin-top: 1px;
    padding: 0;
    flex-shrink: 0;
  }

  .check-row input[type="checkbox"]:checked {
    accent-color: ${COLORS.navy};
  }

  .check-text {
    font-size: 0.88rem;
    color: ${COLORS.text};
    line-height: 1.55;
    cursor: pointer;
    flex: 1;
  }

  .izjave-error {
    font-size: 0.76rem;
    color: ${COLORS.danger};
    margin-top: 0.45rem;
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }
`;

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export default function NalogIsplata() {
  const [form, setForm] = useState({
    imePrezime: "", adresaObjekta: "", oib: "",
    iban: "", email: "", telefon: "",
  });
  const [file, setFile] = useState(null);
  const [izjave, setIzjave] = useState({ izjava1: false, izjava2: false });
  const [errors, setErrors] = useState({});
  const [modalImage, setModalImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
    e.target.value = "";
  };

  const validate = () => {
    const e = {};
    if (!form.imePrezime.trim()) e.imePrezime = "Obavezno polje";
    if (!form.adresaObjekta.trim()) e.adresaObjekta = "Obavezno polje";
    if (!/^\d{11}$/.test(form.oib.trim())) e.oib = "OIB mora imati točno 11 znamenki";
    if (!form.iban.trim()) e.iban = "Obavezno polje";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Unesite valjanu e-mail adresu";
    if (!form.telefon.trim()) e.telefon = "Obavezno polje";
    if (!file) e.file = "Prilog račun je obavezan";
    if (!izjave.izjava1 || !izjave.izjava2) e.izjave = "Morate potvrditi obje izjave";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);

    const fd = new FormData();
    fd.append("Name", form.imePrezime.trim());
    fd.append("Adress", form.adresaObjekta.trim());
    fd.append("Oib", form.oib.trim());
    fd.append("Email", form.email.trim());
    fd.append("Phone", form.telefon.trim());
    fd.append("IBAN", form.iban.trim());
    if (file) fd.append("Files", file);

    
    fetch("https://api.solin.hr/api/SeptickeJame/submit", {
      method: "POST",
      body: fd,
    })
      .then(async (response) => {
        const text = await response.text();
        console.log("BE status:", response.status, "body:", text);
        let message = "Zahtjev je uspješno poslan.";
        try { const d = JSON.parse(text); if (d.message) message = d.message; } catch (_) {}
        setSubmitted(true);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
        alert("Došlo je do pogreške: " + error.message);
      })
      .finally(() => setSubmitting(false));
  };

  if (submitted) {
    return (
      <>
        <style>{css}</style>
        <div className="page-wrap">
          <div className="logo-area">
            <img src="https://www.solin.hr/wp-content/uploads/2019/11/solin-grb-moj-64x77.png" alt="Grad Solin" />
          </div>
          <div className="success-card">
            <div className="success-icon">✅</div>
            <h2>Zahtjev je uspješno poslan</h2>
            <p>Vaši podaci su primljeni. Grad Solin će vas kontaktirati na navedenu e-mail adresu u najkraćem mogućem roku.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="page-wrap">
        <div className="logo-area">
          <img src="https://www.solin.hr/wp-content/uploads/2019/11/solin-grb-moj-64x77.png" alt="Grad Solin" />
        </div>

        <div className="card">
          <div className="card-header">
            <h1>Zahtjev za isplatu sredstava temeljem Zaključka o financiranju usluga čišćenja septičkih jama na području Grada Solina</h1>
            <p>Grad Solin · Obrazac za korisnike</p>
          </div>

          <div className="card-body">
            <form onSubmit={handleSubmit} noValidate>

              {/* Osobni podaci */}
              <div className="section">
                <div className="section-title">Osobni podaci</div>

                <div className="field">
                  <label htmlFor="imePrezime">Ime i prezime <span className="req">*</span></label>
                  <input
                    id="imePrezime" name="imePrezime" type="text"
                    autoComplete="name" placeholder="Unesite ime i prezime"
                    value={form.imePrezime} onChange={handleChange}
                  />
                  {errors.imePrezime && <span className="error-msg">{errors.imePrezime}</span>}
                </div>

                <div className="field">
                  <label htmlFor="adresaObjekta">Adresa objekta <span className="req">*</span></label>
                  <input
                    id="adresaObjekta" name="adresaObjekta" type="text"
                    autoComplete="street-address" placeholder="Ulica, kućni broj, mjesto"
                    value={form.adresaObjekta} onChange={handleChange}
                  />
                  {errors.adresaObjekta && <span className="error-msg">{errors.adresaObjekta}</span>}
                </div>

                <div className="field">
                  <label htmlFor="oib">OIB <span className="req">*</span></label>
                  <input
                    id="oib" name="oib" type="text" inputMode="numeric"
                    maxLength={11} placeholder="11-znamenkasti OIB"
                    autoComplete="off" value={form.oib} onChange={handleChange}
                  />
                  {errors.oib && <span className="error-msg">{errors.oib}</span>}
                </div>
              </div>

              {/* Kontakt */}
              <div className="section">
                <div className="section-title">Kontakt</div>
                <div className="row">
                  <div className="field">
                    <label htmlFor="email">E-mail adresa <span className="req">*</span></label>
                    <input
                      id="email" name="email" type="email" inputMode="email"
                      autoComplete="email" placeholder="ime@domena.hr"
                      value={form.email} onChange={handleChange}
                    />
                    {errors.email && <span className="error-msg">{errors.email}</span>}
                  </div>
                  <div className="field">
                    <label htmlFor="telefon">Broj telefona <span className="req">*</span></label>
                    <input
                      id="telefon" name="telefon" type="tel" inputMode="tel"
                      autoComplete="tel" placeholder="+385 91 234 5678"
                      value={form.telefon} onChange={handleChange}
                    />
                    {errors.telefon && <span className="error-msg">{errors.telefon}</span>}
                  </div>
                </div>
              </div>

              {/* Bankovni */}
              <div className="section">
                <div className="section-title">Bankovni podaci</div>
                <div className="field">
                  <label htmlFor="iban">IBAN <span className="req">*</span></label>
                  <input
                    id="iban" name="iban" type="text"
                    autoComplete="off" placeholder="HR00 0000 0000 0000 0000 0"
                    value={form.iban}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^A-Z0-9]/gi, "").toUpperCase();
                      const formatted = raw.match(/.{1,4}/g)?.join(" ") ?? raw;
                      setForm(prev => ({ ...prev, iban: formatted }));
                      if (errors.iban) setErrors(prev => ({ ...prev, iban: null }));
                    }}
                  />
                  {errors.iban && <span className="error-msg">{errors.iban}</span>}
                </div>
              </div>

              {/* Upload */}
              <div className="section">
                <div className="section-title">Prilog — račun <span className="req" style={{fontFamily:"Mulish,sans-serif",fontSize:"0.85rem"}}>*</span></div>
                <div
                  className={`upload-zone ${file ? "has-file" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    capture="environment"
                    onChange={handleFileChange}
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="upload-icon-wrap">📎</div>
                  <div className="upload-text">
                    {file ? "Zamijeni datoteku" : "Priložite račun"}
                  </div>
                  <div className="upload-hint">
                    Fotografirajte kamerom ili odaberite datoteku<br />JPG, PNG ili PDF
                  </div>
                </div>

                {file && (
                  <div className="file-preview">
                    {file.type.startsWith("image/") ? (
                      <img
                        className="file-preview-thumb"
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        onClick={() => setModalImage(URL.createObjectURL(file))}
                      />
                    ) : (
                      <div className="file-preview-icon">📄</div>
                    )}
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <div className="file-preview-name">{file.name}</div>
                      <div className="file-preview-size">{formatFileSize(file.size)}</div>
                    </div>
                    <button
                      type="button"
                      className="file-remove"
                      onClick={() => { setFile(null); setErrors(prev => ({ ...prev, file: null })); }}
                      aria-label="Ukloni datoteku"
                    >×</button>
                  </div>
                )}
                {errors.file && <span className="error-msg">{errors.file}</span>}
              </div>

              {/* Izjave */}
              <div className="section">
                <div className="section-title">Izjave</div>
                <div className={`izjave-box ${errors.izjave ? "has-error" : ""}`}>
                  <div className="izjave-header">Pod materijalnom i kaznenom odgovornošću</div>
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={izjave.izjava1}
                      onChange={e => {
                        setIzjave(prev => ({ ...prev, izjava1: e.target.checked }));
                        if (errors.izjave) setErrors(prev => ({ ...prev, izjave: null }));
                      }}
                    />
                    <span className="check-text">
                      Izjavljujem da nemam dugovanje prema Gradu Solinu
                    </span>
                  </label>
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={izjave.izjava2}
                      onChange={e => {
                        setIzjave(prev => ({ ...prev, izjava2: e.target.checked }));
                        if (errors.izjave) setErrors(prev => ({ ...prev, izjave: null }));
                      }}
                    />
                    <span className="check-text">
                      Potvrđujem da su svi podaci točni i istiniti
                    </span>
                  </label>
                </div>
                {errors.izjave && (
                  <div className="izjave-error">⚠ {errors.izjave}</div>
                )}
              </div>

              <div className="submit-wrap">
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? "Slanje..." : "Pošalji zahtjev"}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>

      {modalImage && (
        <div className="modal-overlay" onClick={() => setModalImage(null)}>
          <div className="modal-inner" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setModalImage(null)}>×</button>
            <img src={modalImage} alt="Pregled računa" />
          </div>
        </div>
      )}
    </>
  );
}
